import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockCompanies, mockProjects, mockUsers } from '../mock/data';
import { 
  Building2, Plus, Search, MoreVertical, Globe, Mail, Phone, 
  MapPin, Edit2, Trash2, ExternalLink, FolderKanban, Users
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import HeaderGlow from '../components/HeaderGlow';

const Companies = () => {
  const { currentColorTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid, list

  // Get unique industries
  const industries = [...new Set(mockCompanies.map(c => c.industry))];

  // Filter companies
  const filteredCompanies = mockCompanies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         company.industry.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesIndustry = selectedIndustry === 'all' || company.industry === selectedIndustry;
    return matchesSearch && matchesIndustry;
  });

  // Get stats for a company
  const getCompanyStats = (companyId) => {
    const companyProjects = mockProjects.filter(p => p.companyId === companyId);
    const activeProjects = companyProjects.filter(p => p.status === 'active').length;
    const totalProgress = companyProjects.reduce((acc, p) => acc + p.progress, 0);
    const avgProgress = companyProjects.length > 0 ? Math.round(totalProgress / companyProjects.length) : 0;
    
    return {
      totalProjects: companyProjects.length,
      activeProjects,
      avgProgress
    };
  };

  return (
    <div className="p-8 bg-background min-h-screen">
      {/* Header */}
      <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-cyan-500 via-blue-500 to-purple-500'} -mx-8 -mt-8 p-8`}>
        <h1 className="text-3xl font-bold text-white">Companies</h1>
        <p className="text-white/80">Manage your client companies and their projects</p>
      </div>
      {/* Glowing separator line */}
      <div className="-mx-8">
        <HeaderGlow />
      </div>

      {/* Filters & Actions - with top margin */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 mt-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-[280px]"
            />
          </div>
          
          {/* Sector Filter */}
          <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Sectors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sectors</SelectItem>
              {industries.map(industry => (
                <SelectItem key={industry} value={industry}>{industry}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex border border-border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'rounded-none' : 'rounded-none'}
            >
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'rounded-none' : 'rounded-none'}
            >
              Table
            </Button>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Plus className="w-4 h-4 mr-2" />
                Add Company
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] bg-card">
              <DialogHeader>
                <DialogTitle className="text-foreground">Add New Company</DialogTitle>
              </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-foreground">Company Name *</Label>
                <Input placeholder="Enter company name" className="mt-1" />
              </div>
              <div>
                <Label className="text-foreground">Industry</Label>
                <Select>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map(industry => (
                      <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                    ))}
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-foreground">Website</Label>
                <Input placeholder="https://example.com" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-foreground">Contact Email</Label>
                  <Input type="email" placeholder="contact@company.com" className="mt-1" />
                </div>
                <div>
                  <Label className="text-foreground">Contact Phone</Label>
                  <Input placeholder="+1 (555) 000-0000" className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-foreground">Address</Label>
                <Input placeholder="123 Business Street, City, State" className="mt-1" />
              </div>
              <Button 
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={() => {
                  alert('Company creation will be implemented with backend');
                  setCreateDialogOpen(false);
                }}
              >
                Add Company
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Companies Grid or Table View */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map(company => {
            const stats = getCompanyStats(company.id);
            
            return (
              <Card 
                key={company.id} 
                className="bg-card border-border hover:shadow-lg transition-all group overflow-hidden"
              >
                {/* Header with gradient */}
                <div className="h-2 bg-gradient-to-r from-primary via-secondary to-accent" />
                
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-12 h-12 rounded-lg">
                        <AvatarImage src={company.logo} />
                        <AvatarFallback className="rounded-lg bg-primary/20 text-primary">
                          <Building2 className="w-6 h-6" />
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-semibold text-foreground">{company.name}</h3>
                        <Badge variant="outline" className="text-xs text-foreground mt-1">
                          {company.industry}
                        </Badge>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2 mb-4">
                    {company.website && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Globe className="w-4 h-4" />
                        <a 
                          href={company.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="truncate hover:text-primary transition-colors"
                        >
                          {company.website.replace('https://', '')}
                        </a>
                      </div>
                    )}
                    {company.contactEmail && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{company.contactEmail}</span>
                      </div>
                    )}
                    {company.contactPhone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="w-4 h-4" />
                        <span>{company.contactPhone}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                        <FolderKanban className="w-4 h-4" />
                      </div>
                      <p className="text-lg font-bold text-foreground">{stats.totalProjects}</p>
                      <p className="text-xs text-muted-foreground">Projects</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-green-500 mb-1">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                      </div>
                      <p className="text-lg font-bold text-foreground">{stats.activeProjects}</p>
                      <p className="text-xs text-muted-foreground">Active</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-foreground">{stats.avgProgress}%</p>
                      <p className="text-xs text-muted-foreground">Avg Progress</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                    <Button variant="outline" size="sm" className="flex-1">
                      <Edit2 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      View Projects
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 font-medium text-muted-foreground">Company</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Sector</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Contact</th>
                  <th className="text-center p-4 font-medium text-muted-foreground">Projects</th>
                  <th className="text-center p-4 font-medium text-muted-foreground">Progress</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map(company => {
                  const stats = getCompanyStats(company.id);
                  return (
                    <tr key={company.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10 rounded-lg">
                            <AvatarFallback className="rounded-lg bg-primary/20 text-primary">
                              <Building2 className="w-5 h-5" />
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-foreground">{company.name}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="text-xs">{company.industry}</Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {company.contactEmail}
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-semibold text-foreground">{stats.totalProjects}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-primary to-secondary"
                              style={{ width: `${stats.avgProgress}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{stats.avgProgress}%</span>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="sm">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {filteredCompanies.length === 0 && (
        <div className="text-center py-16">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No companies found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery || selectedIndustry !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first company'}
          </p>
          {!searchQuery && selectedIndustry === 'all' && (
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Company
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Companies;
